import { Set } from 'immutable';
import { Base64 } from 'js-base64';
import { isProduction, parse, toString } from '../utils';
import { MemoryStorage } from './memoryStorageClass';

/**
 * Test engine
 * @param engine
 * @returns {boolean}
 */
export function testEngine(engine?: any) {
    if (!engine) {
        return false;
    }

    try {
        engine = typeof engine === 'function' ? engine() : engine;
        engine.setItem('test', 'test');
        engine.getItem('test');
        engine.removeItem('test');

        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Create engine for localStorage
 * @returns {MemoryStorage|Storage}
 */
export const engine$ = (engine: any, fallbackStorage: any) => {
    if (testEngine(engine)) {
        return typeof engine === 'function' ? engine() : engine;
    } else {
        return typeof fallbackStorage === 'function' ? fallbackStorage() : fallbackStorage;
    }
};

export interface StorageOptions {
    defaultValue?: any;
    encode?: boolean;
    parser?: (value: any, options: StorageOptions) => any;
}

const DEFAULT_OPTIONS = {
    encode: isProduction(),
};

export class Storage {
    _engine: any;
    _keys: Set<string>;
    _default: StorageOptions;

    /**
     * Set engine which will be used
     * @param engine
     * @param options
     */
    constructor(engine: any, options: StorageOptions = DEFAULT_OPTIONS) {
        this._engine = engine;
        this._keys = Set<string>();

        this._default = options;
    }

    /**
     * Get number of elements
     * @returns {*}
     */
    get length() {
        return this._engine.length;
    }

    /**
     * Check if is memory storage
     * @returns {boolean}
     */
    public isMemoryStorage() {
        return this._engine instanceof MemoryStorage;
    }

    /**
     * Check if we can encode a string
     * @param options
     * @returns {*}
     * @private
     */
    private _canEncode(options: StorageOptions) {
        return options.encode;
    }

    /**
     * Encode value to base64
     * @param value
     * @returns {string}
     * @private
     */
    private _encode(value: string): string {
        return Base64.encode(toString(value));
    }

    /**
     * Decode value from base64
     * @param value
     * @returns {string}
     * @private
     */
    private _decode(value: string): string {
        return Base64.decode(value);
    }

    /**
     * Get item from storage
     * @param key
     * @param options
     * @returns {T | *}
     */
    public getItem<T = any>(key: string, options?: StorageOptions | null): T {
        let decode = false;
        const encodedKey = this._encode(key);

        options = this._options(options);

        if (!this.hasItem(key) && !this.hasItem(encodedKey)) {
            return options.defaultValue as any;
        }

        if (this.hasItem(encodedKey)) {
            decode = true;
            key = encodedKey;
        }

        let value = this._engine.getItem(key);

        if (decode) {
            value = this._decode(value);
        }

        return this._parser(value, options.parser);
    }

    /**
     * Set item into storage
     * @param key
     * @param value
     * @param options
     * @returns {MemoryStorage}
     */
    public setItem(key: string, value: any, options?: StorageOptions | null) {
        options = this._options(options);
        value = toString(value);

        if (this._canEncode(options)) {
            key = this._encode(key);
            value = this._encode(value);
        }

        this._engine.setItem(key, value);
        this._keys = this._keys.add(key);

        return this;
    }

    /**
     * Remove item from storage
     * @param key
     * @param _options
     * @returns {MemoryStorage}
     */
    public removeItem(key: string, _options?: StorageOptions | null) {
        this._engine.removeItem(key);
        this._engine.removeItem(this._encode(key));

        this._keys = this._keys.delete(key);
        this._keys = this._keys.delete(this._encode(key));

        return this;
    }

    /**
     * Check if item exist into storage
     * @param key
     * @returns {boolean}
     */
    public hasItem(key: string) {
        if (typeof this._engine.hasItem === 'function') {
            return this._engine.hasItem(key) || this._engine.hasItem(this._encode(key));
        }

        return this._engine.hasOwnProperty(key) || this._engine.hasOwnProperty(this._encode(key));
    }

    /**
     * Clear storage
     * @returns {MemoryStorage}
     */
    public clear() {
        this._engine.clear();
        this._keys = this._keys.clear();

        return this;
    }

    /**
     * Parse final value
     * @param value
     * @param parser
     * @returns {*}
     * @private
     */
    private _parser(value: any, parser: any) {
        value = parse(value);
        return typeof parser === 'function' ? parser(value) : value;
    }

    /**
     * Get keys
     * @param raw
     * @returns {*}
     */
    public keys<T extends boolean>(raw?: T): T extends true ? Set<string> : string[] {
        return raw ? this._keys : (this._keys.toJS() as any);
    }

    /**
     *
     * @param options
     * @returns {{}}
     * @private
     */
    private _options(options: StorageOptions | null = {}): StorageOptions {
        return {
            ...(this._default as any),
            ...(options || {}),
        };
    }

    /**
     * Set encode
     * @param encode
     */
    public setEncode(encode = true) {
        this._default.encode = encode;
    }
}
